import type {
  CoreConfigurationOptions,
  FirstPartyHost,
  RumConfigurationOptions,
  SdkVerbosity,
} from 'expo-datadog';

import type { LogsInitConfiguration as BrowserLogsConfigRaw } from '@datadog/browser-logs';
import type { RumInitConfiguration as WebConfigRaw } from '@datadog/browser-rum';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * ## USAGE
 * ```
 * init(
 *   config: StandardInitParams,
 *   overrides?: ClientSpecificOverrides
 * )
 * ```
 * NOTE: The initial version of this had a single params object -- but it became
 * too hard to parse a single object with all sorts of aliased / overlapped
 * stuff -- updates were a nightmare
 *
 */

// ─────────────────────────────────────────────────────────────────────────────
// ───── NATIVE CORE DEFAULTS ─────────────────────────────────────────────────

export const NATIVE_CORE_DEFAULTS: NativeCoreDefaults = {
  site: 'US1',
  verbosity: 'warn' as SdkVerbosity,
};
type NativeCoreDefaults = Pick<CoreConfigurationOptions, 'site' | 'verbosity'>;

// ─────────────────────────────────────────────────────────────────────────────
// ───── NATIVE RUM DEFAULTS ───────────────────────────────────────────────────

// https://docs.datadoghq.com/real_user_monitoring/reactnative/expo/

export const NATIVE_RUM_DEFAULTS: NativeRumDefaults = {
  nativeCrashReportEnabled: true,
  sessionSampleRate: 100,
  resourceTraceSampleRate: 100,
  telemetrySampleRate: 20, // shared
  trackFrustrations: true, // enabled by default now, in browser RUM

  // @REVIEW: disabling pending cost evaluation
  // trackBackgroundEvents: true,
};
type NativeRumDefaults = Pick<
  RumConfigurationOptions,
  | 'nativeCrashReportEnabled'
  | 'sessionSampleRate'
  | 'resourceTraceSampleRate'
  | 'telemetrySampleRate'
  | 'trackFrustrations'
>;

// ─────────────────────────────────────────────────────────────────────────────
// ───── WEB RUM (+Logs) DEFAULTS ──────────────────────────────────────────────

// https://docs.datadoghq.com/real_user_monitoring/browser/

export const WEB_RUM_DEFAULTS: WebRumDefaults = {
  site: 'datadoghq.com',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackResources: true,
  trackLongTasks: true,
  silentMultipleInit: true,
  trackUserInteractions: true, // called `trackInteractions` in React Native RUM config
  telemetrySampleRate: 20, // shared
  defaultPrivacyLevel: 'mask-user-input',
  trackEarlyRequests: true, // https://docs.datadoghq.com/real_user_monitoring/application_monitoring/browser/monitoring_resource_performance/#early-data-collection
  allowedGraphQlUrls: [/\/graphql/], // https://docs.datadoghq.com/real_user_monitoring/application_monitoring/browser/monitoring_resource_performance/#advanced-options
  traceSampleRate: 100, // https://docs.datadoghq.com/real_user_monitoring/rum_without_limits/
  traceContextInjection: 'sampled' as const, // ensures backend APM sampling decisions are still applied
};
type WebRumDefaults = Omit<Partial<WebConfigNormalized>, InitParamKeys>;

// https://docs.datadoghq.com/logs/log_collection/javascript/

export const WEB_LOGS_DEFAULTS: WebLogsDefaults = {
  forwardErrorsToLogs: true,
  forwardConsoleLogs: 'all',
  forwardReports: 'all',
};
type WebLogsDefaults = Omit<Partial<BrowserLogsConfigNormalized>, InitParamKeys>;

// ・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・
// ・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・
// ・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・
// ・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・・

// ─────────────────────────────────────────────────────────────────────────────
// ── TYPES ────────────────────────────────────────────────────────────────────

export type InitFn = (p1: StandardInitParams, p2?: ClientSpecificOverrides) => Promise<void>;

/** Standard Init Params */
export type RequiredInitParamKeys =
  | 'clientToken'
  | 'env'
  | 'applicationId'
  | 'version'
  | 'serviceName';
export type OptionalInitParamKeys = 'versionSuffix'; // this is a concept in RUM native, but not in web -- so we map + interpolate along with version, for web
export type InitParamKeys = RequiredInitParamKeys | OptionalInitParamKeys;

/** Shared init params — platform-agnostic keys */
export type StandardInitParamsBase = Readonly<{
  clientToken: string;
  env: string;
  applicationId: string;
  version: string;
  serviceName: string;
  versionSuffix?: string;
}>;

export type StandardInitParams = StandardInitParamsBase;

export type StandardInitParamsWeb = StandardInitParamsBase;
export type StandardInitParamsNative = StandardInitParamsBase;

/** ClientSpecificOverrides */
export type ClientSpecificOverrides = Readonly<{
  rumNative?: NativeRumOverrides;
  rumWeb?: Partial<WebConfigRaw>;
  logsWeb?: Partial<BrowserLogsConfigRaw>;
  /**
   * Auto-instrument tap/press interactions as native RUM actions. Defaults to `true`.
   * Set to `false` to disable the `DdRum.addAction` instrumentation — e.g. to work
   * around the native `addAction` HostFunction crash on the New Architecture.
   * (Web has its own knob via `rumWeb.trackUserInteractions`.)
   */
  trackInteractions?: boolean;
}>;

/**
 * Extends `RumConfigurationOptions` to also accept plain `string[]` for `firstPartyHosts`.
 * The lib normalizes strings to `FirstPartyHost` objects internally (v3 SDK migration).
 */
export type NativeRumOverrides = Partial<
  Omit<RumConfigurationOptions, 'firstPartyHosts'> & {
    firstPartyHosts?: (string | FirstPartyHost)[];
  }
>;

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// ───── Normalized Types ──────────────────────────────────────────────────────

// # WEB RUM
// For web we just have to alias, to match the native schema + add versionSuffix
export type WebConfigNormalized = AliasKeys<
  Readonly<{ service: 'serviceName' }>, // de-aliased later
  WebConfigRaw
> &
  Readonly<{ versionSuffix?: string }>;

// # WEB LOGS
// Same for the browser logs
export type BrowserLogsConfigNormalized = AliasKeys<
  Readonly<{ service: 'serviceName' }>, // de-aliased later
  BrowserLogsConfigRaw
> &
  Readonly<{ versionSuffix?: string }>;

// ────────────────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────────────────
// ─── TYPE UTILS ──────────────────────────────────────────────────────────────────

type AliasKeys<T, U> = {
  readonly [K in keyof U as K extends keyof T
    ? T[K] extends string
      ? T[K]
      : never
    : K]: K extends keyof U ? U[K] : never;
};
